/**
 * Settings — theme, local preferences, Knowledge Control token, runtime status.
 * Never displays server secrets or API keys.
 */

import { FormEvent, useEffect, useState } from 'react'
import { api, RuntimeResponse } from '../lib/api'
import { useTheme } from '../contexts/ThemeContext'
import { ReviewerThemeAccent } from '../lib/reviewerTheme'
import {
  clearKnowledgeAdminToken,
  loadKnowledgeAdminToken,
  saveKnowledgeAdminToken,
} from '../lib/knowledgeControlAuth'
import { loadReviewerPreferences, saveReviewerPreferences } from '../lib/preferences'
import { Button } from '../components/design-system/Button'
import { LoadingSpinner } from '../components/design-system/Loading'
import { SettingsProjects } from '../components/SettingsProjects'
import { useLocale } from '../contexts/LocaleContext'

export function SettingsPage() {
  const { t } = useLocale()
  const { themeBase, themeAccent, setThemeBase, setThemeAccent } = useTheme()
  const [runtime, setRuntime] = useState<RuntimeResponse | null>(null)
  const [runtimeError, setRuntimeError] = useState<string | null>(null)
  const [loading, setLoading] = useState(true)
  const [tokenDraft, setTokenDraft] = useState('')
  const [hasToken, setHasToken] = useState(() => Boolean(loadKnowledgeAdminToken()))
  const [prefs, setPrefs] = useState(() => loadReviewerPreferences())
  const [saved, setSaved] = useState(false)

  useEffect(() => {
    api
      .getRuntime()
      .then(setRuntime)
      .catch((err) => setRuntimeError(err instanceof Error ? err.message : t('settings.runtimeFailed')))
      .finally(() => setLoading(false))
  }, [])

  function persistPrefs(next: typeof prefs) {
    setPrefs(next)
    saveReviewerPreferences(next)
    setSaved(true)
    window.setTimeout(() => setSaved(false), 1500)
  }

  function handleToken(event: FormEvent) {
    event.preventDefault()
    if (!tokenDraft.trim()) return
    saveKnowledgeAdminToken(tokenDraft)
    setHasToken(true)
    setTokenDraft('')
  }

  return (
    <div data-testid="settings-page" style={{ display: 'flex', flexDirection: 'column', gap: '24px', maxWidth: '720px' }}>
      <header>
        <h1 style={h1Style}>{t('page.settings')}</h1>
        <p style={subStyle}>{t('settings.lead')}</p>
      </header>

      <section style={cardStyle}>
        <h2 style={h2Style}>{t('settings.appearance')}</h2>
        <label className="text-sm" style={labelStyle}>
          {t('settings.themeBase')}
          <select
            value={themeBase}
            onChange={(e) => setThemeBase(e.target.value as typeof themeBase)}
            style={inputStyle}
          >
            <option value="dark">{t('settings.themeDark')}</option>
            <option value="light">{t('settings.themeLight')}</option>
            <option value="system">{t('settings.themeSystem')}</option>
          </select>
        </label>
        <label className="text-sm" style={labelStyle}>
          {t('settings.themeAccent')}
          <select
            value={themeAccent}
            onChange={(e) => setThemeAccent(e.target.value as ReviewerThemeAccent)}
            style={inputStyle}
          >
            <option value="reviewer-electric-blue">{t('theme.electric')}</option>
            <option value="reviewer-neutral">{t('theme.neutral')}</option>
          </select>
        </label>
      </section>

      <section style={cardStyle}>
        <h2 style={h2Style}>{t('settings.auditDefaults')}</h2>
        <p className="text-sm" style={{ color: 'var(--reviewer-text-secondary)' }}>
          {t('settings.auditDefaultsBody')}
        </p>
        <label className="text-sm" style={{ display: 'flex', gap: '8px', alignItems: 'center' }}>
          <input
            type="checkbox"
            data-testid="settings-default-quick-scan"
            checked={prefs.defaultQuickScan}
            onChange={(e) => persistPrefs({ ...prefs, defaultQuickScan: e.target.checked })}
          />
          {t('settings.defaultQuick')}
        </label>
        <label className="text-sm" style={{ display: 'flex', gap: '8px', alignItems: 'center' }}>
          <input
            type="checkbox"
            data-testid="settings-default-include-ai"
            checked={prefs.defaultIncludeAi}
            onChange={(e) => persistPrefs({ ...prefs, defaultIncludeAi: e.target.checked })}
          />
          {t('settings.defaultAi')}
        </label>
        {saved && <p className="text-xs" style={{ color: 'var(--reviewer-accent)' }}>{t('settings.saved')}</p>}
      </section>

      <SettingsProjects />

      <section style={cardStyle}>
        <h2 style={h2Style}>{t('settings.kc')}</h2>
        <p className="text-sm" style={{ color: 'var(--reviewer-text-secondary)' }}>
          {t('settings.kcBody')}
        </p>
        {hasToken ? (
          <div style={{ display: 'flex', gap: '8px', alignItems: 'center' }}>
            <span className="text-sm">{t('settings.tokenPresent')}</span>
            <Button
              variant="secondary"
              size="sm"
              onClick={() => {
                clearKnowledgeAdminToken()
                setHasToken(false)
              }}
            >
              {t('settings.clearToken')}
            </Button>
          </div>
        ) : (
          <form onSubmit={handleToken} style={{ display: 'flex', gap: '8px' }}>
            <input
              type="password"
              value={tokenDraft}
              onChange={(e) => setTokenDraft(e.target.value)}
              placeholder={t('settings.pasteToken')}
              style={{ ...inputStyle, flex: 1 }}
              autoComplete="off"
            />
            <Button type="submit" size="sm">{t('settings.saveTab')}</Button>
          </form>
        )}
      </section>

      <section style={cardStyle}>
        <h2 style={h2Style}>{t('settings.runtime')}</h2>
        {loading && <LoadingSpinner text={t('settings.runtimeLoading')} />}
        {runtimeError && <p className="text-sm" style={{ color: 'var(--reviewer-danger)' }}>{runtimeError}</p>}
        {runtime && (
          <dl className="text-sm" style={{ display: 'grid', gridTemplateColumns: '180px 1fr', gap: '8px 16px', margin: 0 }}>
            <dt>{t('settings.product')}</dt>
            <dd style={{ margin: 0 }}>{runtime.app_name} v{runtime.version}</dd>
            <dt>{t('settings.environment')}</dt>
            <dd style={{ margin: 0 }}>{runtime.environment}</dd>
            <dt>{t('settings.fixAi')}</dt>
            <dd style={{ margin: 0 }}>
              {runtime.orchestrator_configured ? t('settings.orchOn') : t('settings.orchOff')}
            </dd>
            <dt>{t('settings.kcServer')}</dt>
            <dd style={{ margin: 0 }}>
              {runtime.knowledge_admin_configured ? t('settings.kcOn') : t('settings.kcOff')}
            </dd>
            <dt>{t('settings.formats')}</dt>
            <dd style={{ margin: 0 }}>{runtime.available_report_formats.join(', ')}</dd>
          </dl>
        )}
      </section>
    </div>
  )
}

const h1Style = {
  margin: 0,
  marginBottom: '8px',
  fontFamily: 'var(--reviewer-font-mono)',
  fontSize: 'var(--reviewer-text-2xl)',
  fontWeight: 500,
} as const

const h2Style = {
  margin: '0 0 12px',
  fontFamily: 'var(--reviewer-font-mono)',
  fontSize: '16px',
} as const

const subStyle = {
  margin: 0,
  fontFamily: 'var(--reviewer-font-mono)',
  color: 'var(--reviewer-text-secondary)',
} as const

const cardStyle = {
  padding: '20px',
  border: '1px solid var(--reviewer-border)',
  backgroundColor: 'var(--reviewer-surface)',
  display: 'flex',
  flexDirection: 'column' as const,
  gap: '12px',
}

const labelStyle = { display: 'flex', flexDirection: 'column' as const, gap: '6px' }

const inputStyle = {
  padding: '8px 12px',
  fontFamily: 'var(--reviewer-font-mono)',
  backgroundColor: 'var(--reviewer-bg)',
  border: '1px solid var(--reviewer-border)',
  color: 'var(--reviewer-text)',
} as const
